// Taşeron portalı — Belge Analizi (doc-check) oturum kabı: TEK yükleme kuyruğu + tek seferlik
// başlatma çağrısı + "aktif analiz" kimliği (api-contract §20, plan S17-D11/D12 → S19-D3/D5,
// tasarım §8). `AppLayout` içinde BİR KEZ monte edilir; Belgeler VE her personelin doküman
// sayfası (iki ÜRETİCİ) buraya besler, tray + gelecekteki sonuç sayfası bunu okur — rota
// değişse de durum kaybolmaz (S17-D13).
//
// SAHİPLİK SINIRI: bu dosya dosya-SEÇİM arayüzünü İÇERMEZ (dropzone/tür-doğrulama/kapasite
// göstergesi sayfanın/modalın işi) — yalnız "dosyalar seçildi"ktEN sonrasını yönetir: OKUNABİLİR
// (`isAnalyzableDocumentFile`) dosyaların §8 yükleme (create→PUT→confirm) ilerlemesi/iptali,
// modal ÇÖZÜLÜNCE (S19-D3) onaylanan documentUuid[]'in `POST .../document-analyses` ile TEK
// seferlik ERTELENMİŞ başlatılması, `409` kuyruk sinyalinin sessizce yeniden denenmesi ve
// 202'nin `uuid`'idir (yoklama aralığını `useDocumentAnalysis` — o hook zaten var — okur).
// OKUNAMAYAN dosyalar bu dosyadan hiç GEÇMEZ — `startUpload` onları çağırana (modal) döner,
// modal onları tarayıcı belleğinde tutar ve `Kaydet`e kadar hiçbir uç ÇAĞRILMAZ (S19-D1).
//
// DÖRT ZORUNLU KURAL — S19'DA HEPSİ BİRDEN GEÇERLİ, ÜÇÜ S17'DEN KORUNUR:
//
//   1) `principal.companyId` DEĞİŞİNCE kuyruk + bu dosyanın TÜM türetilmiş durumu (aday
//      `subjectUserUuid`, onaylanan uuid'ler, aktif/engelleyen analiz, kuyrukta-bekleme)
//      TAMAMEN temizlenir VE sürmekte olan yüklemeler `AbortController.abort()` ile GERÇEKTEN
//      iptal edilir. Sorgu anahtarları companyId taşır (S17-D12, `supplierContractorKeys.
//      analysis`/`analyses`) ama bu BELLEK-İÇİ kuyruk taşımaz — onsuz A firmasının analizi B
//      firmasının oturumunda render edilir.
//   2) Başlatma çağrısı BAŞARISIZ olursa (kuyruk sinyali DIŞINDA — bkz. Kural 4) onaylanan
//      documentUuid[] KORUNUR — 503 gün-1 durumudur (S17-D10) ve dosyalar zaten yüklenmiş +
//      §20/S17-D3 gereği "Belgelerim"de GÖRÜNMEZDİR; "Tekrar dene" (`retryStart`) AYNI
//      uuid'lerle yeniden POST eder, yeniden YÜKLEMEZ. Sayfa yenilemesinde kaybolmaları kabul
//      edilebilir bir maliyettir (tasarım §8) — kalıcılaştırma YOK.
//   3) `startDocumentAnalysis` BİR TEK yerden çağrılır (burası) ve ANCAK modal ÇÖZÜLÜNCE
//      (`resolveUpload`) tetiklenir — okunabilir dosyaların YÜKLEMESİ seçimde başlar (hız için),
//      ama ANALİZ başlatma çağrısı `Tamam`/`Esc`/`✕` ile ERTELENİR (S19-D3). Anahtar `analyze`
//      SUNUCUYA hiç GİTMEZ (S19-D2) — yalnız ikinci çağrının HANGİSİ olacağını seçer.
//      `analyze=false` → bu partide `startDocumentAnalysis` HİÇBİR KOŞULDA çağrılmaz (AC26);
//      onaylanan uuid'ler manuel forma (bu dosyanın DIŞINDA, D6) linklenmek üzere KALIR.
//   4) `409 DOCUMENT_ANALYSIS_IN_PROGRESS` bir HATA DEĞİL, bir KUYRUK sinyalidir (S19-D5): iki
//      üretici TEK taşeron-başına slotu paylaştığından rutindir. `waitingForSlot=true` olur,
//      onaylanan uuid'ler KORUNUR, `startError` set EDİLMEZ (kullanıcıya "başarısız" gösterilmez)
//      ve engelleyen analiz TERMİNAL olunca OTOMATİK yeniden denenir — `messageData.
//      analysisUuid` DOLUYSA o analiz (aynı `useDocumentAnalysis` yoklama mantığıyla) izlenir;
//      BOŞSA (firma kuyruk derinliği ya da dar-pencere yarışı, api-contract.md:2528) izlenecek
//      bir kimlik YOKTUR — sözleşmenin kendi ifadesiyle KISA SÜRE SONRA körlemesine yeniden
//      denenir.
//
// LİSTE SORGULARI (§20.3, YENİ S19) `useDocumentAnalysis.ts`de yaşar (`useDocumentAnalysisList`)
// — bu dosya yalnız `active` süzgecini bir KEZ (firma başına) okuyup, bellek-içi kuyruk BOŞSA,
// sayfa yenilemesinde tray'in yoklamayı yeniden BAĞLAMASI için `analysisUuid`'i doldurur
// (AC12). `awaiting-review` süzgeci Belgeler'in bekleyen şeridi içindir (D7, bu dosyanın işi
// DEĞİL) — o hook doğrudan çağrılır, context'ten GEÇMEZ.

import {
  ApiError,
  documentAnalysesApi,
  documentsApi,
  isAnalyzableDocumentFile,
  useAuthStore,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSupplierContractorAccess } from './supplierContractorAccess';
import { useDocumentAnalysis, useDocumentAnalysisList } from './useDocumentAnalysis';

export type UploadItemStatus = 'pending' | 'uploading' | 'done' | 'failed';

/** Kuyruktaki tek dosyanın durumu. `id` istemci-taraflı kararlı anahtardır (dizi indeksi DEĞİL). */
export interface UploadQueueItem {
  id: string;
  name: string;
  size: number;
  status: UploadItemStatus;
  /** 0..100; yalnız `uploading` sırasında anlamlıdır. */
  progress: number;
  /** `done` olunca dolar — analiz başlatmada kullanılan onaylı belge kimliği. */
  documentUuid: string | null;
  /** Yalnız `uploading` sırasında dolu — iptal (`removeQueueItem`) bunu çağırır. */
  controller: AbortController | null;
}

interface DocumentAnalysisContextValue {
  /** Yalnız OKUNABİLİR dosyalar — okunamayanlar bu kuyruğa hiç GİRMEZ (Sahiplik sınırı). */
  queue: UploadQueueItem[];
  /** Onaylanmış belge kimlikleri — başarısız başlatma çağrısından SONRA da KORUNUR (Kural 2). */
  confirmedDocumentUuids: string[];
  analysisUuid: string | null;
  /** `POST .../document-analyses` sürüyor mu (ilk çağrı ya da kuyruk sonrası otomatik yeniden
   * deneme). */
  starting: boolean;
  /** Son başlatma hatası — `409` (kuyruk sinyali, Kural 4) burada HİÇBİR ZAMAN görünmez, yalnız
   * GERÇEK başarısızlıklar (503, 429, vb.). Başarıyla değişince temizlenir. */
  startError: ApiError | null;
  /** `409 DOCUMENT_ANALYSIS_IN_PROGRESS` tutuluyor — "Analiz kuyrukta" olarak sunulur (Kural 4),
   * bir HATA değil. Engelleyen analiz terminal olunca kendiliğinden `false`'a döner. */
  waitingForSlot: boolean;
  /**
   * Seçilen dosyaları OKUNABİLİR/OKUNAMAYAN diye ikiye ayırır; okunabilir olanları HEMEN
   * yükler (analiz başlatmaz — Kural 3), okunamayanları YÜKLEMEDEN çağırana döner (S19-D1) —
   * çağıran (modal) bunları tarayıcı belleğinde tutup türü/tarihleri doldurulunca kendi
   * yükleme+link akışını (§8 `POST /documents` + §13.2/§15 link) yürütür.
   * `subjectUserUuid` — personel yüzeyinden başlatıldıysa o personelin uuid'i (§20.1); Belgeler'den
   * başlatıldıysa `undefined`/`null`.
   */
  startUpload: (files: File[], options?: { subjectUserUuid?: string | null }) => File[];
  /** Bekleyen bir dosyayı kaldırır ya da süren yüklemesini iptal eder (AC1 + AC4, S17-D2). */
  removeQueueItem: (id: string) => void;
  /** Sürmekte olan TÜM yüklemeleri iptal eder (tray'in "hepsini iptal et" düğmesi). */
  cancelAllUploads: () => void;
  /** Başlatma GERÇEKTEN başarısız olduysa (Kural 2) AYNI belge kimlikleriyle yeniden dener. */
  retryStart: () => void;
  /**
   * Yükleme modalı ÇÖZÜLÜNCE (Tamam/Esc/✕ — üçü de AYNI) TEK sefer çağrılır (Kural 3).
   * `analyze=true` → okunabilir dosyaların yüklemesi bitmişse HEMEN, bitmemişse ayarlanır
   * ayarlanmaz `startDocumentAnalysis` tetiklenir. `analyze=false` → bu partide
   * `startDocumentAnalysis` HİÇBİR KOŞULDA çağrılmaz (AC26) — modelin OKUYACAĞI tek bir bayt
   * bile YOKTUR.
   */
  resolveUpload: (analyze: boolean) => void;
}

const DocumentAnalysisContext = createContext<DocumentAnalysisContextValue | null>(null);

/** İzlenecek bir uuid TAŞIMAYAN `409` için körlemesine yeniden deneme aralığı (Kural 4b,
 * api-contract.md:2528 — "kısa süre sonra yeniden dener"). Sunucu aralığı YOKTUR burada;
 * `messageData.analysisUuid` DOLU olan varyant onun yerine GERÇEK sunucu-güdümlü yoklamayı
 * (`useDocumentAnalysis`) kullanır. */
const QUEUE_BLIND_RETRY_MS = 4000;

export function DocumentAnalysisProvider({ children }: { children: ReactNode }) {
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  // Önceki firma kimliği — yalnız GERÇEK değişimi yakalamak için (efekt bağımlılığı DEĞİL).
  const companyIdRef = useRef(companyId);

  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const [confirmedDocumentUuids, setConfirmedDocumentUuids] = useState<string[]>([]);
  const [subjectUserUuid, setSubjectUserUuid] = useState<string | null>(null);
  const [analysisUuid, setAnalysisUuid] = useState<string | null>(null);
  const [waitingForSlot, setWaitingForSlot] = useState(false);
  // `409`'un `messageData.analysisUuid`'i — DOLUYSA engelleyen analiz KENDİ taşeronumuzundur ve
  // onun terminal olmasını yoklarız; BOŞSA izlenecek kimlik yoktur (Kural 4b).
  const [blockingAnalysisUuid, setBlockingAnalysisUuid] = useState<string | null>(null);

  // Süren yükleme PARTİLERİNİN canlı kümesi — PARTİ BAŞINA, paylaşımlı bir bayrak DEĞİL
  // (S17 güvenlik incelemesi F2). Her `startUpload` çağrısı kendine özgü bir belirteç (`Symbol`)
  // ekler; partinin ertelenmiş `.then`'i kendi belirtecinin HÂLÂ kümede olup olmadığına bakar.
  // `cancelAllUploads`/firma değişimi kümeyi TAMAMEN boşaltır — böylece o anda uçuştaki HER
  // parti (yalnız "en son başlayan" değil) iptal sayılır. Eski tek-paylaşımlı boolean, yeni bir
  // `startUpload`'ın bayrağı `false`'a geri çekmesiyle ESKİ partinin iptalini geri alıyordu.
  const activeBatchesRef = useRef<Set<symbol>>(new Set());
  // Mevcut partinin okunabilir yüklemeleri AYARLANDI mı (Promise.allSettled bitti mi) —
  // `resolveUpload(true)` modal, yüklemeler SÜRERKEN çözülürse `pendingAnalyzeIntentRef`'i
  // kullanır (Kural 3). Batch'siz/ilk durumda `true` (başlatacak bir yükleme yok).
  const uploadsSettledRef = useRef(true);
  // `resolveUpload(true)` ayarlanmayı BEKLERKEN çağrıldıysa niyeti taşır — yüklemeler bitince
  // OTOMATİK başlatma tetiklenir. Yeni bir `startUpload`/iptal onu SIFIRLAR.
  const pendingAnalyzeIntentRef = useRef(false);
  // Bu partinin `resolveUpload`'ı ZATEN çağrıldı mı — çift tık/çift Esc gibi bir ikinci çağrının
  // AC8'i (parti başına TEK başlatma çağrısı) bozmasını ÖNLER.
  const resolvedRef = useRef(false);

  const startMutation = useMutation<
    { kind: 'started'; uuid: string } | { kind: 'queued'; blockingAnalysisUuid: string | null },
    ApiError,
    { companyId: number | null; documentUuids: string[]; subjectUserUuid: string | null }
  >({
    mutationFn: async ({ documentUuids, subjectUserUuid: subjectForRequest }) => {
      try {
        const analysis = await documentAnalysesApi.startDocumentAnalysis({
          documentUuids,
          subjectUserUuid: subjectForRequest,
        });
        return { kind: 'started', uuid: analysis.uuid };
      } catch (err) {
        // `409 DOCUMENT_ANALYSIS_IN_PROGRESS` bir başarısızlık DEĞİL, bir kuyruk sonucudur
        // (Kural 4) — mutasyonun `error`/`isError`'ına HİÇ düşmez, `onSuccess`'in `queued`
        // dalına akar. Başka her hata (503/429/400/404) OLDUĞU gibi fırlatılır.
        if (err instanceof ApiError && err.errorCode === 'DOCUMENT_ANALYSIS_IN_PROGRESS') {
          const uuid =
            typeof err.messageData?.analysisUuid === 'string' ? err.messageData.analysisUuid : null;
          return { kind: 'queued', blockingAnalysisUuid: uuid };
        }
        throw err;
      }
    },
    // Parti başlatıldığı firmayla damgalanır (S17 güvenlik incelemesi F1). `reset()`
    // uçuştaki POST'u İPTAL ETMEZ (React Query yalnız observer'ı ayırır) — bu yüzden `onSuccess`
    // sonucu YALNIZ istek başlarken aktif olan firma HÂLÂ aktifse uygular; aradan firma
    // değiştiyse (companyIdRef güncellendiyse) geç dönen sonuç sessizce düşülür.
    onSuccess: (result, variables) => {
      if (variables.companyId !== companyIdRef.current) {
        return;
      }
      if (result.kind === 'started') {
        setAnalysisUuid(result.uuid);
        setWaitingForSlot(false);
        setBlockingAnalysisUuid(null);
      } else {
        setWaitingForSlot(true);
        setBlockingAnalysisUuid(result.blockingAnalysisUuid);
      }
    },
  });

  const startAnalysis = useCallback(
    (documentUuids: string[], subjectForBatch: string | null) => {
      // Hepsi başarısız yüklendiyse başlatacak bir şey yok — sessizce çık (kuyruk zaten
      // başarısız satırları gösteriyor, ayrıca bir hata çağrısı UYDURMAYIZ).
      if (documentUuids.length === 0) {
        return;
      }
      startMutation.mutate({
        companyId: companyIdRef.current,
        documentUuids,
        subjectUserUuid: subjectForBatch,
      });
    },
    [startMutation.mutate],
  );

  const startUpload = useCallback(
    (files: File[], options?: { subjectUserUuid?: string | null }): File[] => {
      if (files.length === 0) {
        return [];
      }
      const subjectForBatch = options?.subjectUserUuid ?? null;
      const readableFiles = files.filter(isAnalyzableDocumentFile);
      const unreadableFiles = files.filter((file) => !isAnalyzableDocumentFile(file));

      // Yeni parti — önceki partinin TÜM türetilmiş durumu sıfırlanır. `queue` yalnız
      // OKUNABİLİR dosyaları taşır (Sahiplik sınırı) — okunamayanlar aşağıda hiç `setQueue`'a
      // girmeden döner.
      setSubjectUserUuid(subjectForBatch);
      setConfirmedDocumentUuids([]);
      setAnalysisUuid(null);
      setWaitingForSlot(false);
      setBlockingAnalysisUuid(null);
      startMutation.reset();
      pendingAnalyzeIntentRef.current = false;
      resolvedRef.current = false;

      if (readableFiles.length === 0) {
        uploadsSettledRef.current = true;
        setQueue([]);
        return unreadableFiles;
      }

      uploadsSettledRef.current = false;
      // `{file, item}` çiftleri BİRLİKTE üretilir — `noUncheckedIndexedAccess` altında
      // ayrı dizileri paralel indekslemek (`files[index]`) `File | undefined` üretirdi.
      const entries = readableFiles.map((file) => ({
        file,
        item: {
          id: crypto.randomUUID(),
          name: file.name,
          size: file.size,
          status: 'pending' as const,
          progress: 0,
          documentUuid: null,
          controller: null,
        } satisfies UploadQueueItem,
      }));
      setQueue(entries.map((e) => e.item));
      const batchToken = Symbol('upload-batch');
      activeBatchesRef.current.add(batchToken);

      void Promise.allSettled(
        entries.map(async ({ file, item }) => {
          const controller = new AbortController();
          setQueue((prev) =>
            prev.map((q) => (q.id === item.id ? { ...q, status: 'uploading', controller } : q)),
          );
          try {
            const document = await documentsApi.uploadDocument(file, {
              signal: controller.signal,
              onProgress: (percent) =>
                setQueue((prev) =>
                  prev.map((q) => (q.id === item.id ? { ...q, progress: percent } : q)),
                ),
            });
            setQueue((prev) =>
              prev.map((q) =>
                q.id === item.id
                  ? {
                      ...q,
                      status: 'done',
                      progress: 100,
                      documentUuid: document.uuid,
                      controller: null,
                    }
                  : q,
              ),
            );
            return document.uuid;
          } catch {
            // İptal DAHİL her yükleme hatası burada düşer — iptal edilen öğe zaten
            // `removeQueueItem` ile listeden çıkarılmış olabilir (bu güncelleme o
            // durumda no-op'tur); kalan bir hata ise satır "Yüklenemedi" gösterir.
            setQueue((prev) =>
              prev.map((q) =>
                q.id === item.id ? { ...q, status: 'failed', controller: null } : q,
              ),
            );
            return null;
          }
        }),
      ).then((results) => {
        // Kendi belirteci artık kümede değilse bu PARTİ iptal edilmiştir (cancelAllUploads/
        // firma değişimi) — bazı dosyalar bu arada `done` olmuş olsa BİLE arka planda sessizce
        // analiz BAŞLATILMAZ. Başka bir partinin sonradan başlayıp bitmesi bu partiyi ETKİLEMEZ
        // (F2) — kontrol PARTİYE ÖZGÜ belirteç üzerindendir, paylaşımlı bir bayrak değil.
        if (!activeBatchesRef.current.delete(batchToken)) {
          return;
        }
        const uuids = results
          .map((r) => (r.status === 'fulfilled' ? r.value : null))
          .filter((v): v is string => v !== null);
        setConfirmedDocumentUuids(uuids);
        uploadsSettledRef.current = true;
        // Modal yüklemeler SÜRERKEN `analyze=true` ile ÇÖZÜLMÜŞTÜ (Kural 3) — ayarlanır
        // ayarlanmaz ERTELENMİŞ başlatma şimdi tetiklenir.
        if (pendingAnalyzeIntentRef.current) {
          pendingAnalyzeIntentRef.current = false;
          startAnalysis(uuids, subjectForBatch);
        }
      });

      return unreadableFiles;
    },
    [startAnalysis, startMutation.reset],
  );

  const resolveUpload = useCallback(
    (analyze: boolean) => {
      if (resolvedRef.current) {
        return; // Tamam/Esc/✕ TEK sefer çözülür (AC8) — ikinci çağrı İKİNCİ bir POST YAPMAZ.
      }
      resolvedRef.current = true;
      if (!analyze) {
        pendingAnalyzeIntentRef.current = false;
        return; // AC26 — bu partide startDocumentAnalysis HİÇBİR KOŞULDA tetiklenmez.
      }
      if (uploadsSettledRef.current) {
        startAnalysis(confirmedDocumentUuids, subjectUserUuid);
      } else {
        pendingAnalyzeIntentRef.current = true;
      }
    },
    [confirmedDocumentUuids, subjectUserUuid, startAnalysis],
  );

  const removeQueueItem = useCallback((id: string) => {
    setQueue((prev) => {
      const item = prev.find((q) => q.id === id);
      item?.controller?.abort();
      return prev.filter((q) => q.id !== id);
    });
  }, []);

  const cancelAllUploads = useCallback(() => {
    activeBatchesRef.current.clear();
    pendingAnalyzeIntentRef.current = false;
    uploadsSettledRef.current = true;
    resolvedRef.current = true;
    setQueue((prev) => {
      for (const item of prev) {
        item.controller?.abort();
      }
      return [];
    });
  }, []);

  const retryStart = useCallback(() => {
    startAnalysis(confirmedDocumentUuids, subjectUserUuid);
  }, [confirmedDocumentUuids, subjectUserUuid, startAnalysis]);

  // Kural 4 — `409` kuyruk sinyalinin çözümü. (a) `blockingAnalysisUuid` DOLU: KENDİ
  // taşeronumuzun engelleyen analizini `useDocumentAnalysis`'in AYNI sunucu-güdümlü yoklama
  // mantığıyla izleriz; terminal olunca OTOMATİK yeniden deneriz. (b) BOŞ: izlenecek kimlik
  // yok — sözleşmenin kendi ifadesiyle KISA SÜRE SONRA körlemesine yeniden deneriz.
  const blockingAnalysis = useDocumentAnalysis(waitingForSlot ? blockingAnalysisUuid : null);
  useEffect(() => {
    if (!waitingForSlot) {
      return;
    }
    if (blockingAnalysisUuid !== null) {
      const status = blockingAnalysis.analysis?.analysis.status;
      if (status === 'Succeeded' || status === 'Failed') {
        setWaitingForSlot(false);
        setBlockingAnalysisUuid(null);
        startAnalysis(confirmedDocumentUuids, subjectUserUuid);
      }
      return;
    }
    const timer = window.setTimeout(() => {
      setWaitingForSlot(false);
      startAnalysis(confirmedDocumentUuids, subjectUserUuid);
    }, QUEUE_BLIND_RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [
    waitingForSlot,
    blockingAnalysisUuid,
    blockingAnalysis.analysis,
    confirmedDocumentUuids,
    subjectUserUuid,
    startAnalysis,
  ]);

  // Sayfa yenilemesinde yeniden bağlanma (AC12) — bellek-içi kuyruk BOŞSA (bu sekme HENÜZ tray'i
  // görmediyse) firma başına BİR kez `active` listesine bakılır; bir sonuç varsa `analysisUuid`
  // doldurulur ki `useDocumentAnalysis` yoklamayı devralsın. Bellek-içi bir iş ZATEN varsa
  // (`analysisUuid`/`queue` dolu) sunucudaki BAŞKA bir aktif analiz onu EZMEZ.
  //
  // §20.3 `supplier-contractor` yetkisi ISTER. Bu sağlayıcı `AppLayout`'ta, yani
  // `SupplierContractorGate`'in DIŞINDA monte edilir — yalnız `supplier-self` (Personeli) bir
  // kullanıcı için istek GARANTİLİ `403 COMPANY_NOT_ACCESSIBLE` döner (güvenlik incelemesi F4).
  // Bu yüzden var olan kapı sondası (`supplierContractorKeys.access`) `enabled`'a bağlanır: kapı
  // bu çağrının da TEK doğruluk kaynağıdır. `AppLayout` sondayı zaten çağırdığından (menü
  // görünürlüğü) EK bir istek doğmaz — AYNI sorgu anahtarı, TanStack tekilleştirir.
  const access = useSupplierContractorAccess();
  const activeAnalyses = useDocumentAnalysisList('active', {
    enabled: access.status === 'granted',
  });
  const reattachedForCompanyRef = useRef<number | null>(null);
  useEffect(() => {
    if (companyId == null || reattachedForCompanyRef.current === companyId) {
      return;
    }
    if (activeAnalyses.isPending) {
      return;
    }
    reattachedForCompanyRef.current = companyId;
    if (analysisUuid !== null || queue.length > 0) {
      return;
    }
    const first = activeAnalyses.items[0];
    if (first) {
      setAnalysisUuid(first.uuid);
    }
  }, [companyId, activeAnalyses.isPending, activeAnalyses.items, analysisUuid, queue.length]);

  // Kural 1 — firma DEĞİŞİNCE kuyruğu + bu dosyanın TÜM türetilmiş durumunu boşalt + sürmekte
  // olan yüklemeleri GERÇEKTEN iptal et. `companyIdRef` ile karşılaştırma efektin bağımlı
  // dizisinde OLMAMALI (ref reaktif değildir); yalnız GERÇEK bir değişimi yakalamak için
  // kullanılır, ilk mount'ta hiçbir şey yapmaz.
  useEffect(() => {
    if (companyIdRef.current === companyId) {
      return;
    }
    companyIdRef.current = companyId;
    activeBatchesRef.current.clear();
    pendingAnalyzeIntentRef.current = false;
    uploadsSettledRef.current = true;
    resolvedRef.current = true;
    setQueue((prev) => {
      for (const item of prev) {
        item.controller?.abort();
      }
      return [];
    });
    setConfirmedDocumentUuids([]);
    setSubjectUserUuid(null);
    setAnalysisUuid(null);
    setWaitingForSlot(false);
    setBlockingAnalysisUuid(null);
    startMutation.reset();
  }, [companyId, startMutation.reset]);

  const value: DocumentAnalysisContextValue = {
    queue,
    confirmedDocumentUuids,
    analysisUuid,
    starting: startMutation.isPending,
    startError: startMutation.error,
    waitingForSlot,
    startUpload,
    removeQueueItem,
    cancelAllUploads,
    retryStart,
    resolveUpload,
  };

  return (
    <DocumentAnalysisContext.Provider value={value}>{children}</DocumentAnalysisContext.Provider>
  );
}

/** `DocumentAnalysisProvider` altında kullanılır — `AppLayout` bunu her zaman monte eder. */
export function useDocumentAnalysisQueue(): DocumentAnalysisContextValue {
  const ctx = useContext(DocumentAnalysisContext);
  if (!ctx) {
    throw new Error('useDocumentAnalysisQueue, DocumentAnalysisProvider dışında çağrıldı.');
  }
  return ctx;
}
